"use client"

import { useState } from "react"
import { useLocale, useTranslations } from "next-intl"
import { usePathname, getPathname } from "@/i18n/navigation"
import { routing } from "@/i18n/routing"

// EN / ES text toggle for marketing chrome (nav + footer). No flag icons —
// just the language codes, with the active locale emphasized.
//
// Switching does a FULL navigation (window.location), not a client-side
// router transition: NextIntlClientProvider lives in the root layout, which
// Next.js does not re-render on soft navigation — a soft switch changes the
// URL but leaves the old language's messages mounted. A hard navigation
// server-renders the page in the new locale. We also persist NEXT_LOCALE
// explicitly so the choice survives reload and carries into the app zone.
export function LocaleSwitcher({ className = "" }: { className?: string }) {
  const t = useTranslations("marketing.localeSwitcher")
  const locale = useLocale()
  const pathname = usePathname()
  const [isPending, setIsPending] = useState(false)

  const switchTo = (next: string) => {
    if (next === locale) return
    setIsPending(true)
    document.cookie = `NEXT_LOCALE=${next}; path=/; max-age=${60 * 60 * 24 * 365}; samesite=lax`
    // `pathname` is the locale-agnostic path (next-intl strips the prefix);
    // getPathname re-applies the right prefix for the target locale
    // ("/" -> "/es", "/es/x" -> "/x" when switching back to en).
    window.location.assign(getPathname({ href: pathname, locale: next as "en" | "es" }))
  }

  return (
    <div
      className={`inline-flex items-center gap-1.5 text-[13px] font-medium tracking-tight ${className}`}
      role="group"
      aria-label={t("label")}
    >
      {routing.locales.map((code, i) => {
        const active = code === locale
        return (
          <span key={code} className="inline-flex items-center">
            {i > 0 && <span className="mx-1 opacity-40" aria-hidden>·</span>}
            <button
              type="button"
              onClick={() => switchTo(code)}
              disabled={isPending}
              aria-current={active ? "true" : undefined}
              className={`transition-opacity hover:opacity-100 ${
                active ? "font-semibold underline underline-offset-4" : "opacity-60"
              }`}
            >
              {t(code as "en" | "es")}
            </button>
          </span>
        )
      })}
    </div>
  )
}
